import '@/components/button/styles.scss';

import { memo, useMemo } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';

import { BASE_URL } from '@/services/Constants';

import Bevel from '@/components/button/Bevel';
import { type ButtonProps } from '@/components/button/types';
import Spinner from '@/components/spinner/Spinner';

import type { MouseEvent, Ref } from 'react';

const Button = ({
	active,
	additionalSubTitle,
	ariaLabel,
	avaName,
	avaOutside,
	btnType = 'submit',
	buttonRef,
	children,
	className,
	color,
	dataName,
	description,
	disabled,
	href,
	iconClassName = 'icon',
	iconName,
	imgAlt = 'img',
	imgSrc,
	id,
	label,
	labelColor,
	link,
	loading,
	onClick,
	onFocus,
	onMouseDown,
	rounded,
	small,
	subTitle,
	tabIndex = -1,
	title,
	type = 'primary',
	style,
	testId,
}: ButtonProps) => {
	const hasBevel = useMemo(
		() => ['primary', 'secondary'].includes(type) && !disabled,
		[type, disabled],
	);

	const bevelColor = useMemo(() => {
		if (color) return color;
		if (type === 'primary') return 'yellow';
		if (type === 'secondary') return 'sand';
		return undefined;
	}, [type, color]);

	const buttonClassName = classNames(
		type ? `btn-${type}` : null,
		color,
		{
			disabled,
			loading,
			small,
			rounded,
			active: active || loading,
		},
		!hasBevel && className,
		description ? 'sub-desc' : null,
	);

	const handleClick = (e: MouseEvent) => {
		if (disabled || loading) return;
		onClick?.(e);
	};

	const renderContent = () => (
		<>
			{avaName && (
				<div className="avatar">
					<img
						src={(avaOutside ? '' : BASE_URL) + avaName}
						alt="subjectLogo"
						width="24px"
						height="24px"
					/>
				</div>
			)}
			{iconName && !avaName && !loading && (
				<span className={classNames(iconClassName, iconName)} />
			)}
			{imgSrc && <img className="image" src={imgSrc} alt={imgAlt} />}
			{title && !subTitle && !description && <span className="text">{title}</span>}
			{title && description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text desc">{description}</span>
				</div>
			)}
			{title && subTitle && !description && (
				<div className="desc-block">
					<span className="text">{title}</span>
					<span className="text sub-text">{subTitle}</span>
					{additionalSubTitle && (
						<span className="text sub-text">{additionalSubTitle}</span>
					)}
				</div>
			)}
			{loading && <Spinner />}
			{label && <span className={classNames('label-wrapper', labelColor)}>{label}</span>}
			{children}
		</>
	);

	const renderVisual = () =>
		['primary', 'secondary'].includes(type) ? (
			<div className="visual">{renderContent()}</div>
		) : (
			renderContent()
		);

	const renderElement = () => {
		if (href) {
			return (
				<a
					id={id}
					href={href}
					className={buttonClassName}
					rel="noopener noreferrer"
					target="_blank"
					aria-label={ariaLabel}
					data-name={dataName}
					data-testid={testId}
					ref={buttonRef as Ref<HTMLAnchorElement>}
				>
					{renderVisual()}
				</a>
			);
		}

		if (link) {
			return (
				<Link
					id={id}
					tabIndex={tabIndex}
					to={link}
					className={buttonClassName}
					onClick={handleClick}
					aria-label={ariaLabel}
					data-name={dataName}
					data-testid={testId}
					style={style}
					ref={buttonRef as Ref<HTMLAnchorElement>}
				>
					{renderVisual()}
				</Link>
			);
		}

		return (
			<button
				id={id}
				className={buttonClassName}
				onClick={handleClick}
				onFocus={onFocus}
				onMouseDown={onMouseDown}
				tabIndex={tabIndex}
				type={btnType}
				disabled={disabled}
				aria-label={ariaLabel}
				data-name={dataName}
				data-testid={testId}
				style={style}
				ref={buttonRef as Ref<HTMLButtonElement>}
			>
				{renderVisual()}
			</button>
		);
	};

	if (hasBevel) {
		return (
			<Bevel
				className={className}
				color={bevelColor}
				disabled={disabled}
				isPressed={active || loading}
			>
				{renderElement()}
			</Bevel>
		);
	}

	return renderElement();
};

export default memo(Button);
